(() => {
  if (window.TartePromotedAtb) return;

  const PRIVATE_PROPERTIES = Object.freeze({
    version: '_tarte_atb_version',
    promotion: '_tarte_atb_promotion',
    source: '_tarte_atb_source',
    groupId: '_tarte_atb_group_id',
    slot: '_tarte_atb_slot',
    requiredCount: '_tarte_atb_required_count',
  });

  function createGroupId() {
    const id = window.crypto?.randomUUID?.()
      || `${Date.now().toString(36)}-${Math.random().toString(36).slice(2)}`;
    return `atb-${id.toLowerCase()}`;
  }

  function isPromotionConfigured(promotion, source) {
    return promotion?.enabled === true
      && !!promotion?.campaignKey
      && Number(promotion?.percentage) > 0
      && Number(promotion?.percentage) <= 100
      && Array.isArray(promotion?.allowedSources)
      && promotion.allowedSources.includes(source);
  }

  function isPromotionEligible(item, promotion, promotionEnabled = true) {
    if (!promotionEnabled || !item) return false;
    const ids = promotion?.eligibilityType === 'variants'
      ? promotion.eligibleVariantIds
      : promotion?.eligibleProductIds;
    const targetId = promotion?.eligibilityType === 'variants'
      ? String(item.variantId)
      : item.productId;
    return Array.isArray(ids) && ids.map(String).includes(targetId);
  }

  function getQualificationState({ allItems = [], behavior = {}, promotion, promotionEnabled = false }) {
    const participatingItems = allItems.filter((item) => !item.excluded);
    const availableItems = participatingItems.filter((item) => item.available);
    const selectedItems = availableItems.filter((item) => item.selected);
    const selectedSlots = new Set(selectedItems.map((item) => item.slot));
    const requiredItems = behavior.qualifyWithoutOutOfStock
      ? participatingItems.filter((item) => item.available || item.hasAvailableVariant)
      : participatingItems;
    const allRequiredSelected = requiredItems.length > 0
      && requiredItems.every((item) => item.available && selectedSlots.has(item.slot));
    const selectionQualified = selectedItems.length > 0
      && (!behavior.allProductsMustBeSelected || allRequiredSelected);
    const selectedPromotionEligible = promotionEnabled
      && selectedItems.length > 0
      && selectedItems.every((item) => isPromotionEligible(item, promotion, promotionEnabled));

    return {
      allItems,
      participatingItems,
      availableItems,
      selectedItems,
      selectedSlots,
      selectionQualified,
      selectedPromotionEligible,
      discountQualified: selectionQualified && selectedPromotionEligible,
    };
  }

  function formatMoney(cents) {
    const formatter = window.TarteCurrencyFormatter;
    if (formatter && typeof formatter.formatCents === 'function') {
      return formatter.formatCents(cents, { trimTrailingZeros: true });
    }
    const amount = Number(cents) / 100;
    return `$${Number.isInteger(amount) ? amount : amount.toFixed(2)}`;
  }

  function discountedPrice(cents, percentage) {
    return Math.round(Number(cents) * (100 - Number(percentage || 0)) / 100);
  }

  function buildItems({ campaignKey, source, groupId = createGroupId(), requiredCount, items }) {
    const normalizedItems = Array.isArray(items) ? items : [];
    const expectedCount = Number(requiredCount ?? normalizedItems.length);

    if (!campaignKey || !source) throw new Error('ATB promotion campaign and source are required.');
    if (!Number.isSafeInteger(expectedCount) || expectedCount < 1) {
      throw new Error('ATB promotion requiredCount must be a positive integer.');
    }

    return normalizedItems.map((item) => {
      if (!item?.id || !item?.slot) throw new Error('Each promoted ATB item requires an id and slot.');

      return {
        id: item.id,
        quantity: Number(item.quantity) > 0 ? Number(item.quantity) : 1,
        properties: {
          ...(item.properties || {}),
          [PRIVATE_PROPERTIES.version]: '1',
          [PRIVATE_PROPERTIES.promotion]: String(campaignKey),
          [PRIVATE_PROPERTIES.source]: String(source),
          [PRIVATE_PROPERTIES.groupId]: String(groupId),
          [PRIVATE_PROPERTIES.slot]: String(item.slot),
          [PRIVATE_PROPERTIES.requiredCount]: String(expectedCount),
        },
      };
    });
  }

  async function requestCartAdd(items, sections = []) {
    const response = await fetch(`${window.Shopify.routes.root}cart/add.js`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ items, sections }),
    });
    const payload = await response.json();

    if (!response.ok || payload.status) {
      const error = new Error(payload.description || `HTTP ${response.status}`);
      error.status = response.status;
      error.payload = payload;
      throw error;
    }

    return payload;
  }

  async function addCartItems({ items = [], sections = [] }) {
    return requestCartAdd(items, sections);
  }

  async function addItems({ campaignKey, source, groupId, requiredCount, items, sections = [] }) {
    const promotedItems = buildItems({ campaignKey, source, groupId, requiredCount, items });
    return requestCartAdd(promotedItems, sections);
  }

  async function retryAfterAvailabilityRefresh(
    error,
    { retried = false, refreshAvailability, retry } = {},
  ) {
    if (error?.status !== 422 || retried) return false;
    if (typeof refreshAvailability !== 'function' || typeof retry !== 'function') {
      throw new Error('ATB availability retry requires refreshAvailability and retry callbacks.');
    }
    await refreshAvailability();
    await retry();
    return true;
  }

  window.TartePromotedAtb = Object.freeze({
    addCartItems,
    addItems,
    buildItems,
    createGroupId,
    discountedPrice,
    formatMoney,
    getQualificationState,
    isPromotionConfigured,
    isPromotionEligible,
    properties: PRIVATE_PROPERTIES,
    retryAfterAvailabilityRefresh,
  });
})();
